import {
  Box,
  styled
} from "@mui/material";
import AddLink from "controls/global/add-link";
import ConfirmationDialogWithProgressbar from "controls/global/dialogs/confirmation-dialog-with-progressbar";
import ConfirmationDialogV3 from "controls/global/dialogs/confirmation-dialog/ConfirmationDialogV3";
import { showToast } from "controls/global/stewart-toast/showToast";
import {
  ActionResult,
  SCFile,
  TitleSearchProduct
} from "entities/UIModel";
// import CompanyStateLocation from "entities/UIModel/company/CompanyStateLocation";
import TitleSearch from "pages/file/components/product-section/title-search/TitleSearch";
import mapUiSCFileToApiSCFile from "pages/file/utils/toApi/mapUiSCFileToApiSCFile";
import { runValidation } from "pages/file/utils/yup/validator";
import React, {
  useRef
} from "react";
import { useFieldArray } from "react-hook-form";
import { gaps } from "theme/defaultStyle";
import { useAutomaticProgressDialogActions } from "utils/context/AutomaticProgressDialogContext";
import { useCompanyLocation } from "utils/context/CompanyLocationContext";
import { useFiles } from "utils/context/FilesContext";
import { useProcessStatusTracking } from "utils/context/ProcessStatusContext";
import useCreateFile from "utils/custom-hooks/useCreateFile";
// import useFieldDisabler from "utils/custom-hooks/useFieldDisabler";
import useFocus from "utils/custom-hooks/useFocus";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
// import useTitleSearchErrorHandler from "utils/custom-hooks/useTitleSearchErrorHandler";
import { useTitleSearchMessageIntegrationKey } from "utils/context/TitleSearchContext";
import {
  MapActionType,
  ProductType,
  ProductAction,
  ProductTypeName,
  ActionResultStatus
} from "utils/data/enum";
import { getDefaultTitleSearchProduct } from "utils/data/products";
import { v4 as uuidv4 } from "uuid";
import { OrderUpdateDialogHandle } from "./OrderUpdateDialog";
import { useSpaceAgencyConfig } from "utils/context/SpaceContext";
import useFieldDisabler from "utils/custom-hooks/useFieldDisabler";

const StyledGroup = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.large1,
});

const SUBMIT_SUCCESS = "Title Search submitted successfully!";
const SUBMIT_FAILED = "Title Search submission failed!";
const RESEND_TO_STEWART_SUCCESS = "Title Search successfully routed to Stewart!";
const RESEND_TO_STEWART_FAILED = "Failed to submit to Stewart!";
const UPDATE_SUCCESS = "Updated order successfully";
const UPDATE_FAILED = "Order Update failed";
const CANCEL_SUCCESS = 'Title Search cancelled successfully!';
const CANCEL_FAILED = 'Title Search cancellation failed!';
const CANCEL_UPDATE_SUCCESS = 'Update successfully cancelled!';
const CANCEL_UPDATE_FAILED = 'Update cancelation failed!';

const TitleSearchCollection = () => {
  const [{ tsAgencyConfig }] = useSpaceAgencyConfig();
  const orderUpdateDialogRef = useRef<OrderUpdateDialogHandle>(null)  ;
  const [, { setMessageIntegrationKey }] = useTitleSearchMessageIntegrationKey();
  const [currentOrderUpdateKey, setCurrentOrderUpdateKey] = React.useState<string>("");
  const { getValues, setValue, clearErrors, trigger } = useFormWrapper();
  const [{ companyStateLocations }] = useCompanyLocation();
  const { resetValidationRequiredFlags, setLocationInfoWithDisplayName } = useCreateFile();
  const [, { openAutomaticProgressDialog, closeAutomaticProgressDialog }] = useAutomaticProgressDialogActions();
  const [, { deleteOrder }] = useFiles();
  const { reloadFile } = useCreateFile();

  const { fields, append, remove } = useFieldArray({ name: "titleSearchProducts" });
  const isTitleSearchDisabled =
    !(tsAgencyConfig &&
      tsAgencyConfig.products &&
      tsAgencyConfig.products.length > 0 &&
      tsAgencyConfig.products[0].name?.toUpperCase() === ProductType.TitleSearch);

  // console.log("isTitleSearchDisabled:", isTitleSearchDisabled);      
  const isFileDisabled = useFieldDisabler("AddTitleSearchButton");
  const titleSearchProducts: TitleSearchProduct[] = getValues("titleSearchProducts");
  const indexToDeleteRef = useRef<number | undefined>();
  const [isDialogOpen, setDialogOpen] = React.useState<boolean>(false);
  // const companyStateLocationsRef = React.useRef<CompanyStateLocation[]>([]);

  const { setFocusInputElement } = useFocus();

  // Constants used in Progress dialog 
  const [, { setBypassDefaultLoader }] = useProcessStatusTracking();
  const [openProgressDialog, setOpenProgressDialog] = React.useState<boolean>(false);
  const [progressMessage, setProgressMessage] = React.useState<string>("");
  const [runProgress, setRunProgress] = React.useState<boolean>(false);
  const [progressRequestId, setProgressRequestId] = React.useState<string>("");

  // Constants used during Submit Title Search
  const [{ initialValues }, { submitTitleSearch }] = useFiles();
  const [submitTitleSearchButtonDisabled, setSubmitTitleSearchButtonDisabled] = React.useState<boolean>(false);
  const [resendToStewartButtonDisabled, setResendToStewartButtonDisabled] = React.useState<boolean>(false);
  
  const currentProductAction = React.useRef<string | undefined>("");
  const currentActionResult = React.useRef<ActionResult>({});
  const currentProductIntegrationKey = React.useRef<string | undefined>("");
  // const { checkError } = useTitleSearchErrorHandler();



  // Functions used during showing Progress Dialog
  const handleOnCloseProgressDialog = async () => {
    setOpenProgressDialog(false);
    setRunProgress(false);
    setProgressRequestId("");

    // Renable Action button
    switch (currentProductAction.current) {
      case ProductAction.Issue.toString():
        setSubmitTitleSearchButtonDisabled(false);
        break;
      case ProductAction.IssueResend.toString():
        setResendToStewartButtonDisabled(false);
        break;
    }    
  };

  const handleCloseProgressAction = () => {
    if (currentProductAction.current &&
      currentProductAction.current === ProductAction.Revise &&
      currentActionResult.current &&
      currentActionResult.current.status === ActionResultStatus.Success
    ) {
      orderUpdateDialogRef.current?.closeDialog();
    }

  }  

  const setDefaultLocation = (productIndex: number) => {
    if (companyStateLocations.length > 0 && productIndex >= 0) {
      const schema = `titleSearchProducts.${productIndex}`;
      setLocationInfoWithDisplayName(schema, companyStateLocations[0]);
    }
  }
   
  const addTitleSearch = () => {
    const newTitleSearch = getDefaultTitleSearchProduct(true);
    newTitleSearch.integrationKey = uuidv4();
    const newItemIndex = titleSearchProducts.length;
    append(newTitleSearch);
    setValue(`titleSearchProducts.0.isDefault`, false);
    setValue(`titleSearchProducts.0.isDirty`, true);

    setFocusInputElement(`titleSearchProducts.${newItemIndex}.transactionType`);
    setDefaultLocation(newItemIndex);
  };

  const handleKeyDown = (evt: KeyboardEvent) => {
    if (evt.key === "Tab" && !evt.shiftKey && document?.getElementById(ProductTypeName.TITLE_SEARCH)) {
      evt.preventDefault();
      document?.getElementById(ProductTypeName.TITLE_SEARCH)?.focus();
    }
  }

  const openDeleteDialog = (deleteIndex: number) => {
    indexToDeleteRef.current = deleteIndex;
    setDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    indexToDeleteRef.current = undefined;
    setDialogOpen(false);
  };

  const deleteItem = async () => {
    if (indexToDeleteRef.current === undefined) return;
    const deleteItemIndex = indexToDeleteRef.current;

    if (titleSearchProducts) {
      remove(deleteItemIndex);

      // Check if the product is saved already
      // If yes, delete from db
      if (titleSearchProducts.length - 1 >= deleteItemIndex) {
        const deleteOrderId = titleSearchProducts[deleteItemIndex].orderID;
        if ((deleteOrderId || 0) > 0) {
          setDialogOpen(false);
          openAutomaticProgressDialog();

          await deleteOrder(titleSearchProducts[deleteItemIndex].orderID);
          await reloadFile();
          closeAutomaticProgressDialog();
        }
      }
    }

    indexToDeleteRef.current = undefined;
    setDialogOpen(false);
  }

  const handleSubmitTitleSearch = async (
    titleSearchProduct: TitleSearchProduct,
    integrationKey: string,
    productAction?: string,
  ) => {
    // Store current action parameters
    currentProductIntegrationKey.current = integrationKey;
    currentProductAction.current = productAction;

    // Clear previous action error
    clearErrors();
    resetValidationRequiredFlags();

    // set validation flags for Title Search related
    // -- set validation county required flag for title search to tru
    setValue(`properties.0.isTitleSearchCountyValidationRequired`, true);
    // -- set validation title serach required flag
    const titleSearchProducts: TitleSearchProduct[] = getValues(
      "titleSearchProducts"
    );
    titleSearchProducts?.forEach((ts, index) => {
      if (ts.integrationKey === integrationKey) {
        if (productAction && [ProductAction.Issue.toString()]
          .includes(productAction)) {
          setValue(`titleSearchProducts.${index}.isValidationRequired`, true);
        }
        else {
          setValue(`titleSearchProducts.${index}.isValidationRequired`, false);
        }

        setValue(`titleSearchProducts.${index}.isUpdateOrderValidationRequired`, false);
      }
    });

    // setValidLocations(companyStateLocationsRef.current);
    const currentValues: SCFile = getValues();
    const isValid = await runValidation({
      values: currentValues,
      trigger,
      setValue,
      productType: ProductType.TitleSearch,
    });

    if (!isValid) return;

    // Disaable Action button
    switch (productAction) {
      case ProductAction.Issue.toString():
        setSubmitTitleSearchButtonDisabled(true);
        break;
      case ProductAction.IssueResend.toString():
        setResendToStewartButtonDisabled(true);
        break;
    }

    // Submit Title Search
    const apiSCFile = mapUiSCFileToApiSCFile(
      initialValues,
      currentValues,
      companyStateLocations, // undefined,
      MapActionType.ProductAction,
      productAction,
      ProductType.TitleSearch,
      [titleSearchProduct.integrationKey]
    );

    const toastTitle = {
      success: '',
      error: '',
      progressBarDialogTitle: 'Please wait while submitting your Title Search...',
    };

    switch (productAction) {
      case ProductAction.Issue:
        toastTitle.success = SUBMIT_SUCCESS;
        toastTitle.error = SUBMIT_FAILED;
        toastTitle.progressBarDialogTitle = "Please wait while submitting your Title Search...";
        break;
      case ProductAction.IssueResend:
        toastTitle.success = RESEND_TO_STEWART_SUCCESS;
        toastTitle.error = RESEND_TO_STEWART_FAILED;
        toastTitle.progressBarDialogTitle = "Please wait while resubmitting your Title Search...";
        break;
      case ProductAction.UndoRevise:
        toastTitle.success = CANCEL_UPDATE_SUCCESS;
        toastTitle.error = CANCEL_UPDATE_FAILED;
        toastTitle.progressBarDialogTitle = "Please wait while cancelling your Title Search Order Update...";
        break;

      case ProductAction.Revise:
        toastTitle.success = UPDATE_SUCCESS;
        toastTitle.error = UPDATE_FAILED;
        toastTitle.progressBarDialogTitle = "Please wait while submitting your Order Update...";
        break;
        case ProductAction.Void:
          toastTitle.success = CANCEL_SUCCESS;
          toastTitle.error = CANCEL_FAILED;
          toastTitle.progressBarDialogTitle = "Please wait while cancelling your Title Search";
          break;
      // default:
      //   return toastTitle;
    }

    // -- show progress dialog
    let progressRequestId = uuidv4();
    setProgressRequestId(progressRequestId);
    setProgressMessage(toastTitle?.progressBarDialogTitle);
    setBypassDefaultLoader(true);
    setOpenProgressDialog(true);
    setRunProgress(true);
    setCurrentOrderUpdateKey("");

    // -- calling issue cpl
    const submit = async () => {
      currentActionResult.current = await submitTitleSearch(
        apiSCFile,
        progressRequestId,
        productAction,
        integrationKey
      );

      if (currentActionResult.current?.status === ActionResultStatus.Failed) {
        showToast({
          type: "error",
          title: toastTitle.error,
        });

      } else {
        showToast({
          type: "success",
          title: toastTitle.success,
        });
        
        if (productAction === ProductAction.Revise || productAction === ProductAction.UndoRevise) {
          setMessageIntegrationKey("");
        }
      }

      if (productAction === ProductAction.Revise || productAction === ProductAction.UndoRevise) {
        setCurrentOrderUpdateKey(integrationKey);
      }

    };
    await submit();
    return currentActionResult.current;
  };

  React.useEffect(() => {
    // companyStateLocationsRef.current = companyStateLocations;
    if (companyStateLocations.length > 0 && titleSearchProducts.length === 1 ) {
      setDefaultLocation(0)
    }
  }, [companyStateLocations]);

  React.useEffect(() => {
    if (titleSearchProducts.length > 0) {
      if (titleSearchProducts.length === 1 ) {
        setDefaultLocation(0)
      }
      return;
    } 
    const newTitleSearch = getDefaultTitleSearchProduct();
    newTitleSearch.integrationKey = uuidv4();
    append(newTitleSearch);
    setValue(`titleSearchProducts.0`, newTitleSearch);
    setDefaultLocation(0);
  }, [append, titleSearchProducts.length, setValue]);

  return (
    <>
        
      <StyledGroup>
        {fields.map((field: any, index: number) => (
          <TitleSearch
            key={field.fieldId}
            ref={orderUpdateDialogRef}
            titleSearchProduct={field}
            productIndex={index}
            isTitleSearchDisabled={isTitleSearchDisabled}
            onDelete={() => openDeleteDialog(index)}
            submitButtonDisabled={submitTitleSearchButtonDisabled || isTitleSearchDisabled}
            resendToStewartButtonDisabled={resendToStewartButtonDisabled || isTitleSearchDisabled}
            onSubmit={async (integrationKey: string, productAction?: string) => {
              return await handleSubmitTitleSearch(field, integrationKey, productAction);
            }}
            currentOrderUpdateKey={currentOrderUpdateKey}
          />
        ))}
        <Box>
          <AddLink
            disabled={isTitleSearchDisabled || isFileDisabled}
            onClick={addTitleSearch}
            onKeyDown={handleKeyDown}
          >
            Add Title Search
          </AddLink>
        </Box>
      </StyledGroup>

      <ConfirmationDialogV3
        confirmationMessage="Delete this pending Title Search?"
        isOpen={isDialogOpen}
        onYes={deleteItem}
        onNo={closeDeleteDialog}
        onCancel={closeDeleteDialog}
        cancelActionButton={true}
        singleActionButtonText={"Delete"}
        buttonNoText={"Cancel"}
      />

      <ConfirmationDialogWithProgressbar
        autoClose={true}
        title={progressMessage}
        closeButtonText="Done! Click to Continue"
        isOpen={openProgressDialog}
        onClose={handleOnCloseProgressDialog}
        requestId={progressRequestId}
        runProgress={runProgress}
        onCloseAction={handleCloseProgressAction}
      />
    </>
  );
}

export default TitleSearchCollection

